import React, { useEffect, useMemo, useState } from 'react';
import {
  BellRing,
  CheckCircle2,
  CircleAlert,
  Headphones,
  Inbox,
  KeyRound,
  MailCheck,
  Siren,
  Users,
} from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationItem from '@/components/NotificationItem';
import { isPlatformSuperadmin, readStoredUser } from '../auth/saasSession';
import './saasSecondaryPages.css';

// Early-warning notification types (ForeSight) — hideable via the institution-user toggle.
const WARNING_TYPES = new Set(['EARLY_WARNING', 'EARLY_WARNING_DISPATCH']);

const SAAS_LANGUAGE_KEY = 'disasterms_saas_language';
const SAAS_LANGUAGE_EVENT = 'disasterms:saas-language-changed';

const FILTERS = [
  { id: 'all', en: 'All', ar: 'الكل' },
  { id: 'licenses', en: 'Licenses', ar: 'التراخيص' },
  { id: 'seats', en: 'Seats', ar: 'المقاعد' },
  { id: 'invitations', en: 'Invitations', ar: 'الدعوات' },
  { id: 'support', en: 'Support', ar: 'الدعم' },
];

const SAAS_COPY = {
  en: {
    title: 'Notifications',
    subtitle: 'Platform alerts across licenses, seats, invitations and support.',
    loading: 'Loading notifications...',
    emptyTitle: 'No notifications here',
    emptyBody: 'New platform activity will appear here.',
    unread: (count) => `${count} unread`,
    caughtUp: 'All caught up',
    notification: 'Notification',
    unreadLabel: 'Unread',
  },
  ar: {
    title: 'الإشعارات',
    subtitle: 'تنبيهات المنصة المتعلقة بالتراخيص والمقاعد والدعوات والدعم.',
    loading: 'جارٍ تحميل الإشعارات...',
    emptyTitle: 'لا توجد إشعارات هنا',
    emptyBody: 'سيظهر نشاط المنصة الجديد هنا.',
    unread: (count) => `${count} غير مقروء`,
    caughtUp: 'تمت قراءة الكل',
    notification: 'إشعار',
    unreadLabel: 'غير مقروء',
  },
};

const notificationCategory = (notification) => {
  const searchable = [
    notification?.type,
    notification?.category,
    notification?.title,
    notification?.message,
  ].filter(Boolean).join(' ').toLowerCase();

  if (/support|break.?glass|emergency access|ticket|grant/.test(searchable)) return 'support';
  if (/invitat|activation|activate account|pending member/.test(searchable)) return 'invitations';
  if (/seat|capacity|named.?user/.test(searchable)) return 'seats';
  if (/licen[cs]e|trial|expir|grace|entitlement|suspend|cancel/.test(searchable)) return 'licenses';
  return 'other';
};

const categoryPresentation = {
  licenses: { Icon: KeyRound, tone: 'sky' },
  seats: { Icon: Users, tone: 'yellow' },
  invitations: { Icon: MailCheck, tone: 'green' },
  support: { Icon: Headphones, tone: 'red' },
  other: { Icon: CircleAlert, tone: 'muted' },
};

const formatNotificationTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const useSaasLanguage = () => {
  const readLanguage = () => localStorage.getItem(SAAS_LANGUAGE_KEY) === 'ar' ? 'ar' : 'en';
  const [language, setLanguage] = useState(readLanguage);

  useEffect(() => {
    const syncLanguage = () => setLanguage(readLanguage());
    window.addEventListener(SAAS_LANGUAGE_EVENT, syncLanguage);
    window.addEventListener('storage', syncLanguage);
    return () => {
      window.removeEventListener(SAAS_LANGUAGE_EVENT, syncLanguage);
      window.removeEventListener('storage', syncLanguage);
    };
  }, []);

  return language;
};

const SaaSNotifications = ({ allNotifications, unreadCount, loading, openNotification, language }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const copy = SAAS_COPY[language];
  const notifications = useMemo(
    () => activeFilter === 'all'
      ? allNotifications
      : allNotifications.filter((notification) => notificationCategory(notification) === activeFilter),
    [activeFilter, allNotifications]
  );

  return (
    <main className="dms-saas-secondary-page" data-testid="saas-notifications-page">
      <section className="dms-saas-secondary-heading">
        <div className="dms-saas-heading-icon"><BellRing aria-hidden="true" /></div>
        <div>
          <h1>{copy.title}</h1>
          <p>{copy.subtitle}</p>
        </div>
        <span className="dms-saas-unread-summary">
          {unreadCount > 0 ? copy.unread(unreadCount) : copy.caughtUp}
        </span>
      </section>

      <section className="dms-saas-card dms-saas-notifications-card" aria-label={copy.title}>
        <div className="dms-saas-filter-row" role="group" aria-label={copy.title}>
          {FILTERS.map((filter) => {
            const count = filter.id === 'all'
              ? allNotifications.length
              : allNotifications.filter((notification) => notificationCategory(notification) === filter.id).length;
            return (
              <button
                key={filter.id}
                type="button"
                aria-pressed={activeFilter === filter.id}
                className={`dms-saas-filter-chip${activeFilter === filter.id ? ' is-active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter[language]}
                <span>{count}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="dms-saas-empty-state">{copy.loading}</div>
        ) : notifications.length > 0 ? (
          <div className="dms-saas-notification-list" data-testid="saas-notification-list">
            {notifications.map((notification) => {
              const category = notificationCategory(notification);
              const { Icon, tone } = categoryPresentation[category] || categoryPresentation.other;
              const unread = notification?.read === false || notification?.read === 0;
              return (
                <button
                  type="button"
                  key={notification.id || notification._id}
                  className={`dms-saas-notification-row${unread ? ' is-unread' : ''}`}
                  onClick={() => openNotification(notification)}
                >
                  <span className={`dms-saas-notification-icon tone-${tone}`}><Icon aria-hidden="true" /></span>
                  <span className="dms-saas-notification-content">
                    <span className="dms-saas-notification-title">{notification.title || copy.notification}</span>
                    {notification.message && <span className="dms-saas-notification-message">{notification.message}</span>}
                  </span>
                  <span className="dms-saas-notification-meta" dir="ltr">
                    {formatNotificationTime(notification.created_at)}
                    {unread && <span className="dms-saas-unread-dot" aria-label={copy.unreadLabel} />}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="dms-saas-empty-state">
            <BellRing aria-hidden="true" />
            <strong>{copy.emptyTitle}</strong>
            <span>{copy.emptyBody}</span>
          </div>
        )}
      </section>
    </main>
  );
};

const Notifications = () => {
  const { notifications: allNotifications, unreadCount, loading, openNotification } = useNotifications();
  const [hideWarnings, setHideWarnings] = useState(() => localStorage.getItem('notificationsHideWarnings') === '1');
  const [activeFilter, setActiveFilter] = useState('all');
  const language = useSaasLanguage();
  const isSaasAccount = isPlatformSuperadmin(readStoredUser());

  const toggleHideWarnings = () => {
    setHideWarnings((v) => {
      const next = !v;
      try { localStorage.setItem('notificationsHideWarnings', next ? '1' : '0'); } catch (e) { /* non-persistent */ }
      return next;
    });
  };

  const warningCount = useMemo(
    () => allNotifications.filter((n) => WARNING_TYPES.has(n.type)).length,
    [allNotifications]
  );
  const notifications = useMemo(
    () => (hideWarnings ? allNotifications.filter((n) => !WARNING_TYPES.has(n.type)) : allNotifications),
    [allNotifications, hideWarnings]
  );
  const visibleUnreadCount = useMemo(
    () => notifications.filter((notification) => notification?.read === false || notification?.read === 0).length,
    [notifications]
  );
  const filteredNotifications = useMemo(
    () => activeFilter === 'unread'
      ? notifications.filter((notification) => notification?.read === false || notification?.read === 0)
      : notifications,
    [activeFilter, notifications]
  );

  if (isSaasAccount) {
    return (
      <SaaSNotifications
        allNotifications={allNotifications}
        unreadCount={unreadCount}
        loading={loading}
        openNotification={openNotification}
        language={language}
      />
    );
  }

  const summary = loading
    ? 'Loading notifications…'
    : unreadCount > 0
      ? `${unreadCount} unread · ${notifications.length} shown`
      : `${notifications.length} shown · all caught up`;

  const emptyForHiddenWarnings = (
    activeFilter === 'all'
    && hideWarnings
    && warningCount > 0
    && notifications.length === 0
  );

  return (
    <div
      className="h-full w-full overflow-y-auto bg-[#f8fafc] px-4 py-5 sm:px-6 lg:px-8 xl:px-10"
      data-testid="notifications-page"
    >
      <div className="mx-auto w-full max-w-[1920px]">
        <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-200">
              <BellRing className="h-6 w-6" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h1 className="text-[26px] font-black tracking-[-0.035em] text-slate-950 sm:text-[28px]">
                Notifications
              </h1>
              <p className="text-[13px] font-medium leading-snug text-slate-500 sm:text-sm" aria-live="polite">
                {summary}
                {hideWarnings && warningCount > 0 && (
                  <> · {warningCount} warning notification{warningCount === 1 ? '' : 's'} hidden</>
                )}
              </p>
            </div>
          </div>

          {warningCount > 0 && (
            <button
              type="button"
              role="switch"
              aria-checked={hideWarnings}
              aria-label={`Hide warning notifications. ${warningCount} available.`}
              onClick={toggleHideWarnings}
              data-testid="hide-warnings-toggle"
              className={`inline-flex h-10 w-fit items-center gap-2.5 whitespace-nowrap rounded-xl border px-3.5 text-[13px] font-bold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                hideWarnings
                  ? 'border-slate-800 bg-slate-900 text-white hover:bg-slate-800'
                  : 'border-rose-200 bg-white text-slate-700 hover:border-rose-300 hover:bg-rose-50/60'
              }`}
            >
              <Siren className={`h-4 w-4 ${hideWarnings ? 'text-rose-300' : 'text-rose-500'}`} aria-hidden="true" />
              <span>Hide warnings</span>
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-black ${
                hideWarnings ? 'bg-white/10 text-rose-200' : 'bg-rose-100 text-rose-700'
              }`}>
                {warningCount}
              </span>
              <span
                className={`relative h-[18px] w-8 rounded-full transition ${
                  hideWarnings ? 'bg-indigo-500' : 'bg-slate-200'
                }`}
                aria-hidden="true"
              >
                <span className={`absolute left-0 top-[3px] h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${
                  hideWarnings ? 'translate-x-[17px]' : 'translate-x-[3px]'
                }`} />
              </span>
            </button>
          )}
        </header>

        <section
          className="min-h-[420px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
          aria-labelledby="notification-feed-title"
        >
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm">
                <Inbox className="h-4 w-4" aria-hidden="true" />
              </span>
              <div>
                <h2 id="notification-feed-title" className="text-[13px] font-extrabold text-slate-900">
                  Notification feed
                </h2>
                <p className="text-[11px] font-medium text-slate-500">
                  Select an item to open its related record
                </p>
              </div>
            </div>

            <div
              className="inline-flex w-fit items-center rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
              role="group"
              aria-label="Notification filters"
            >
              <button
                type="button"
                aria-pressed={activeFilter === 'all'}
                onClick={() => setActiveFilter('all')}
                data-testid="notification-filter-all"
                className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12px] font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  activeFilter === 'all'
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                }`}
              >
                All
                <span className={activeFilter === 'all' ? 'text-slate-300' : 'text-slate-400'}>
                  {notifications.length}
                </span>
              </button>
              <button
                type="button"
                aria-pressed={activeFilter === 'unread'}
                onClick={() => setActiveFilter('unread')}
                data-testid="notification-filter-unread"
                className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12px] font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  activeFilter === 'unread'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-slate-500 hover:bg-indigo-50 hover:text-indigo-700'
                }`}
              >
                Unread
                <span className={activeFilter === 'unread' ? 'text-indigo-100' : 'text-indigo-500'}>
                  {visibleUnreadCount}
                </span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="divide-y divide-slate-100" role="status" aria-live="polite">
              <span className="sr-only">Loading notifications</span>
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="grid animate-pulse grid-cols-[42px_minmax(0,1fr)] gap-3.5 px-4 py-4 sm:px-5">
                  <span className="h-[42px] w-[42px] rounded-xl bg-slate-100" />
                  <span className="space-y-2 pt-0.5">
                    <span className="block h-3.5 w-1/3 rounded bg-slate-100" />
                    <span className="block h-3 w-2/3 rounded bg-slate-100" />
                    <span className="block h-2.5 w-1/4 rounded bg-slate-100" />
                  </span>
                </div>
              ))}
            </div>
          ) : filteredNotifications.length > 0 ? (
            <div className="divide-y divide-slate-100" data-testid="notification-list">
              {filteredNotifications.map((n) => (
              <NotificationItem
                key={n.id || n._id}
                notification={n}
                onClick={openNotification}
              />
            ))}
          </div>
        ) : (
          <div className="flex min-h-[330px] flex-col items-center justify-center px-6 py-12 text-center" role="status">
            <span className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${
              emptyForHiddenWarnings
                ? 'bg-rose-50 text-rose-500'
                : activeFilter === 'unread'
                  ? 'bg-emerald-50 text-emerald-600'
                  : 'bg-slate-100 text-slate-400'
            }`}>
              {emptyForHiddenWarnings
                ? <Siren className="h-6 w-6" aria-hidden="true" />
                : activeFilter === 'unread'
                  ? <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
                  : <BellRing className="h-6 w-6" aria-hidden="true" />}
            </span>
            <p className="text-[14px] font-extrabold text-slate-800">
              {emptyForHiddenWarnings
                ? 'Warning alerts are hidden'
                : activeFilter === 'unread'
                  ? 'You’re all caught up'
                  : 'No notifications yet'}
            </p>
            <p className="mt-1 max-w-sm text-[12px] leading-5 text-slate-500">
              {emptyForHiddenWarnings
                ? 'Show warning alerts to review the latest early-warning activity.'
                : activeFilter === 'unread'
                  ? 'There are no unread notifications in your feed.'
                  : 'Incident updates and early warnings will appear here.'}
            </p>
            {emptyForHiddenWarnings && (
              <button
                type="button"
                onClick={toggleHideWarnings}
                className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3.5 text-[12px] font-bold text-rose-700 shadow-sm transition hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2"
              >
                <Siren className="h-3.5 w-3.5" aria-hidden="true" />
                Show warnings
              </button>
            )}
          </div>
        )}
        </section>
      </div>
    </div>
  );
};

export default Notifications;
